import type { Directive, DirectiveBinding } from 'vue'

interface OverflowTooltipHTMLElement extends HTMLElement {
  _overflowTooltip?: {
    cleanup: () => void
    observer: ResizeObserver
  }
}

function isOverflowing(el: HTMLElement): boolean {
  return el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight
}

let tooltipEl: HTMLElement | null = null
let currentTarget: HTMLElement | null = null
let showTimeout: ReturnType<typeof setTimeout> | null = null
let styleInjected = false

function injectStyles() {
  if (styleInjected) return
  styleInjected = true

  const style = document.createElement('style')
  style.textContent = `
    .v-overflow-tooltip {
      position: fixed;
      z-index: 2400;
      background: rgb(var(--v-theme-on-surface));
      color: rgb(var(--v-theme-surface));
      padding: 5px 16px;
      border-radius: 4px;
      font-size: 0.75rem;
      line-height: 1.375rem;
      max-width: 300px;
      word-wrap: break-word;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);
      font-family: inherit;
    }
    .v-overflow-tooltip--visible {
      opacity: 1;
    }
  `
  document.head.appendChild(style)
}

function ensureTooltipElement(): HTMLElement {
  injectStyles()

  if (!tooltipEl) {
    tooltipEl = document.createElement('div')
    tooltipEl.className = 'v-overflow-tooltip'
    tooltipEl.setAttribute('role', 'tooltip')
    document.body.appendChild(tooltipEl)
  }

  return tooltipEl
}

function showTooltip(el: HTMLElement, text: string) {
  if (showTimeout) {
    clearTimeout(showTimeout)
    showTimeout = null
  }

  showTimeout = setTimeout(() => {
    if (!isOverflowing(el)) return

    const tooltip = ensureTooltipElement()
    tooltip.textContent = text
    currentTarget = el

    // Position below the element
    const rect = el.getBoundingClientRect()
    tooltip.classList.remove('v-overflow-tooltip--visible')
    tooltip.style.left = `${rect.left + rect.width / 2}px`
    tooltip.style.top = `${rect.bottom + 8}px`
    tooltip.style.transform = 'translateX(-50%)'

    requestAnimationFrame(() => {
      tooltip.classList.add('v-overflow-tooltip--visible')

      // Adjust horizontal position if off-screen
      const tooltipRect = tooltip.getBoundingClientRect()
      if (tooltipRect.right > window.innerWidth - 8) {
        tooltip.style.left = `${window.innerWidth - tooltipRect.width - 8}px`
        tooltip.style.transform = 'none'
      }
      if (tooltipRect.left < 8) {
        tooltip.style.left = '8px'
        tooltip.style.transform = 'none'
      }

      // If tooltip goes below viewport, show above instead
      if (tooltipRect.bottom > window.innerHeight - 8) {
        tooltip.style.top = `${rect.top - tooltipRect.height - 8}px`
      }
    })
  }, 300)
}

function hideTooltip() {
  if (showTimeout) {
    clearTimeout(showTimeout)
    showTimeout = null
  }

  if (tooltipEl) {
    tooltipEl.classList.remove('v-overflow-tooltip--visible')
    currentTarget = null
  }
}

export const vOverflowTooltip: Directive = {
  mounted(el: OverflowTooltipHTMLElement, binding: DirectiveBinding) {
    const onMouseEnter = () => {
      if (!isOverflowing(el)) return
      const text = binding.value || el.textContent?.trim() || ''
      if (text) showTooltip(el, text)
    }

    const onMouseLeave = () => {
      hideTooltip()
    }

    el.addEventListener('mouseenter', onMouseEnter)
    el.addEventListener('mouseleave', onMouseLeave)

    const observer = new ResizeObserver(() => {
      if (currentTarget === el && !isOverflowing(el)) {
        hideTooltip()
      }
    })
    observer.observe(el)

    el._overflowTooltip = {
      cleanup: () => {
        el.removeEventListener('mouseenter', onMouseEnter)
        el.removeEventListener('mouseleave', onMouseLeave)
      },
      observer,
    }
  },

  updated(el: OverflowTooltipHTMLElement, binding: DirectiveBinding) {
    if (currentTarget === el) {
      if (!isOverflowing(el)) {
        hideTooltip()
      } else if (tooltipEl) {
        tooltipEl.textContent = binding.value || el.textContent?.trim() || ''
      }
    }
  },

  unmounted(el: OverflowTooltipHTMLElement) {
    if (el._overflowTooltip) {
      el._overflowTooltip.cleanup()
      el._overflowTooltip.observer.disconnect()
    }
    if (currentTarget === el) {
      hideTooltip()
    }
  },
}
